/* eslint-disable react/display-name */
/** @jsx jsx */

import { useState, useEffect, Fragment } from "react"
import PropTypes from "prop-types"
import { jsx, Heading, Input, Button, Flex, Grid, Text } from "theme-ui"
import { Formik, Form, useFormikContext } from "formik"
import { transparentize } from "@theme-ui/color"
import * as Yup from "yup"
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js"
import short from "short-uuid"

import * as CountryCodes from "country-codes-list"
import { SelectField } from "../SelectField"
import { ResponsiveImage } from "../ResponsiveImage"
import { seedlingsAndSoilBackground } from "../../hooks/seedlingsAndSoilBackground"
import { FormikFieldWithError } from "../FormikFieldWithError"
import { TransformedCrystallizeProductPropTypes } from "../../utils/propTypes"
import { SeedlingLoader } from "../SeedlingLoader"
import { Crystallize } from "../../enums/Crystallize"
import { withRecaptcha } from "../../utils/recaptchaUtils"
import { withStripe } from "../../hocs/withStripe"
import { useMutation } from "react-query"
import { ApiClient } from "../../utils/apiClient"
import { Divider } from "../Divider"
import { DividerType } from "../../hooks/divider"
import { formatPrice } from "../../utils/displayUtils"
import { calculateShippingCost } from "../../utils/eCommerceUtils"
import { loadRecaptcha } from "../../utils/recaptchaUtils"
import { useFormAlertContext } from "../../hocs/FormAlertProvider"

const BaseFields = () => {
  const countryCallingCodes = CountryCodes.customList(
    "countryNameEn",
    "{countryNameEn} (+{countryCallingCode})"
  )
  const countryCallingCodeOptions = Object.keys(countryCallingCodes).map(
    country => ({
      label: countryCallingCodes[country],
      value: countryCallingCodes[country],
    })
  )

  return (
    <Fragment>
      <FormikFieldWithError
        component={Input}
        name="firstName"
        id="firstName"
        type="text"
        fieldVariant="inputWhiteNoLabel"
        placeholder="First name"
        aria-label="firstName"
        onFocus={loadRecaptcha}
      />
      <FormikFieldWithError
        component={Input}
        name="lastName"
        id="lastName"
        type="text"
        fieldVariant="inputWhiteNoLabel"
        placeholder="Last name"
        aria-label="lastName"
      />
      <FormikFieldWithError
        component={Input}
        name="email"
        id="email"
        // Do not use email type as native HTML validation
        // interferes with focusing and scrolling to erroring input
        type="text"
        fieldVariant="inputWhiteNoLabel"
        placeholder="Email"
        aria-label="Email"
      />
      <Flex
        sx={{
          gridColumnStart: ["span 1", "span 1", "span 1", "span 2"],
        }}
      >
        <FormikFieldWithError
          component={SelectField}
          name="countryCallingCode"
          id="countryCallingCode"
          options={countryCallingCodeOptions}
          fieldVariant="selectWhiteNoLabel"
          containerStyles={{
            flex: 1,
            display: "inline-block",
            minWidth: "150px",
          }}
        />
        <FormikFieldWithError
          component={Input}
          name="phone"
          id="phone"
          type="text"
          fieldVariant="inputWhiteNoLabel"
          placeholder="Phone Number"
          aria-label="Phone Number"
          containerStyles={{ ml: 3, flex: 1, display: "inline-block" }}
        />
      </Flex>
    </Fragment>
  )
}

const ShippingAddressFields = () => {
  const countries = CountryCodes.customList("countryNameEn", "{countryNameEn}")
  const countryOptions = Object.keys(countries).map(country => ({
    label: country,
    value: country,
  }))

  return (
    <Fragment>
      <Heading
        variant="subheading"
        sx={{
          color: "white",
          gridColumnStart: ["span 1", "span 2", "span 2", "span 3"],
          mt: 3,
        }}
      >
        Shipping Address
      </Heading>
      <FormikFieldWithError
        component={SelectField}
        name="shippingAddress.country"
        id="shippingAddress.country"
        options={countryOptions}
        fieldVariant="selectWhite"
        containerStyles={{ mb: 2 }}
      />
      <FormikFieldWithError
        component={Input}
        name="shippingAddress.city"
        id="shippingAddress.city"
        type="text"
        placeholder="Town/City (optional)"
        fieldVariant="selectWhite"
      />
      <FormikFieldWithError
        component={Input}
        name="shippingAddress.streetAddress"
        id="shippingAddress.streetAddress"
        type="text"
        placeholder="House number and street name"
        fieldVariant="inputWhiteNoLabel"
        containerStyles={{ gridColumnStart: 1 }}
      />
      <FormikFieldWithError
        component={Input}
        name="shippingAddress.unitNumber"
        id="shippingAddress.unitNumber"
        placeholder="Unit no. (optional)"
        fieldVariant="inputWhiteNoLabel"
      />
      <FormikFieldWithError
        component={Input}
        name="shippingAddress.postalCode"
        id="shippingAddress.postalCode"
        placeholder="Postal code"
        fieldVariant="inputWhiteNoLabel"
      />
    </Fragment>
  )
}

const PaymentModeFields = ({ shippingRequired }) => {
  const { values } = useFormikContext()

  const isCustomerInSingapore =
    values.countryCallingCode.includes("Singapore") ||
    (shippingRequired && values.shippingAddress.country === "Singapore")
  const paymentModeOptions = Object.values(Crystallize.PaymentMode)
    .filter(mode =>
      isCustomerInSingapore
        ? mode === Crystallize.PaymentMode.PAYNOW_PAYLAH
        : true
    )
    .map(mode => ({
      label: mode,
      value: mode,
    }))

  return (
    <Fragment>
      <Heading
        variant="subheading"
        sx={{
          color: "white",
          gridColumnStart: ["span 1", "span 2", "span 2", "span 3"],
          mt: 3,
        }}
      >
        Payment Mode
      </Heading>
      <FormikFieldWithError
        component={SelectField}
        name="paymentMode"
        id="paymentMode"
        options={paymentModeOptions}
        fieldVariant="selectWhite"
        containerStyles={{ mb: 2 }}
      />
      {isCustomerInSingapore ? (
        <Text
          variant="fineprint"
          sx={{
            color: "white",
            opacity: 0.8,
            gridColumnStart: ["span 1", "span 1", "span 1", "span 3"],
          }}
        >
          * Please note that we only accept payment via PayNow/PayLah! for
          customers in Singapore, so that payment gateway fees don&apos;t cut
          into our already thin margins. <br />
          Payment instructions will be shown once you have placed your order.
        </Text>
      ) : (
        <Text
          variant="fineprint"
          sx={{
            color: "white",
            opacity: 0.8,
            gridColumnStart: ["span 1", "span 1", "span 1", "span 3"],
          }}
        >
          * We accept payment either via Stripe or PayNow/PayLah! for overseas
          customers. We would appreciate if you pay via PayNow/PayLah! if you
          are able to, so that payment gateway fees don&apos;t cut into our
          already thin margins
        </Text>
      )}
    </Fragment>
  )
}
PaymentModeFields.propTypes = {
  shippingRequired: PropTypes.bool,
}

const StripePaymentFields = ({
  cart,
  shouldCreatePaymentIntent,
  updateStripePaymentIntentData,
}) => {
  const { values, setFieldValue } = useFormikContext()
  const createPaymentIntent = useMutation(
    () => {
      return ApiClient.post("/api/stripe-payment-intent", {
        country: values.shippingAddress.country,
        cart,
        idempotencyKey: short.generate(),
      })
    },
    {
      onSuccess: data => {
        updateStripePaymentIntentData(data)
      },
      retry: 3,
    }
  )

  useEffect(() => {
    if (
      shouldCreatePaymentIntent &&
      values.paymentMode === Crystallize.PaymentMode.STRIPE
    ) {
      createPaymentIntent.mutate()
    }
    // Can't pass the mutation object to the dependency array as the
    // object will change after the mutation is executed, causing an infinite loop
    // We also don't want this to trigger every time the user
    // update the email value in the form
    // (ie. we just want to trigger it the very first time the email value is valid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreatePaymentIntent, values.paymentMode])

  if (values.paymentMode !== Crystallize.PaymentMode.STRIPE) {
    return null
  }

  if (createPaymentIntent.isIdle || createPaymentIntent.isLoading) {
    return <SeedlingLoader />
  }

  if (createPaymentIntent.isError) {
    return (
      <Text
        variant="fineprint"
        sx={{
          color: "errorLight",
        }}
      >
        Unable to connect to Stripe for payment. Please check your network
        connection.
      </Text>
    )
  }

  return (
    // To add billing address
    <div
      sx={{
        py: 3,
        borderBottomColor: "white",
        borderBottomStyle: "solid",
        borderBottomWidth: "1px",
        gridColumnStart: ["span 1", "span 1", "span 1", "span 2"],
      }}
    >
      <CardElement
        onChange={({ empty, complete, error }) => {
          setFieldValue("stripeDetailsValid", !empty && !error && complete)
        }}
        options={{
          style: {
            base: {
              iconColor: "white",
              color: "white",
              fontFamily: "'newsreader', serif",
              fontSize: "20px",
              "::placeholder": {
                color: "rgba(255,255,255, 0.9)",
              },
            },
          },
        }}
      />
    </div>
  )
}
StripePaymentFields.propTypes = {
  cart: PropTypes.arrayOf(
    PropTypes.shape({
      product: PropTypes.object,
      selectedVariant: PropTypes.object,
      quantity: PropTypes.number,
    })
  ),
  shouldCreatePaymentIntent: PropTypes.bool,
  updateStripePaymentIntentData: PropTypes.func,
}

const OrderItem = ({ itemName, itemCost }) => (
  <Flex sx={{ justifyContent: "flex-end", my: 4 }}>
    <Text variant="body" sx={{ mr: "auto", color: "white" }}>
      {itemName}
    </Text>
    <Text variant="body" sx={{ color: "white" }}>
      {formatPrice(itemCost)}
    </Text>
  </Flex>
)
OrderItem.propTypes = {
  itemName: PropTypes.string,
  itemCost: PropTypes.number,
}

const OrderTotal = ({ product, productCost, containerStyles }) => {
  const { values } = useFormikContext()
  const shippingCost = calculateShippingCost(values.shippingAddress.country)

  return (
    <div sx={{ ...containerStyles }}>
      <Divider
        type={DividerType.DEFAULT_WHITE}
        styles={{
          my: 4,
        }}
      />
      <Heading as="h3" variant="subheading" sx={{ color: "white" }}>
        Order Total
      </Heading>
      <OrderItem itemName={product.name} itemCost={productCost} />
      <OrderItem itemName="Shipping" itemCost={shippingCost} />
      <hr sx={{ color: "white" }} />
      <Flex sx={{ justifyContent: "flex-end", my: 4 }}>
        <Text
          variant="body"
          sx={{ mr: "auto", color: "white", fontWeight: "bold" }}
        >
          Total
        </Text>
        <Text variant="body" sx={{ color: "white", fontWeight: "bold" }}>
          {formatPrice(productCost + shippingCost)}
        </Text>
      </Flex>
      <Divider
        type={DividerType.DEFAULT_WHITE}
        styles={{
          my: 4,
        }}
      />
    </div>
  )
}
OrderTotal.propTypes = {
  product: PropTypes.shape(TransformedCrystallizeProductPropTypes),
  productCost: PropTypes.number,
  containerStyles: PropTypes.object,
}

const OrderFormButton = ({
  isLoading,
  isVisible,
  shouldRetryStripePayment,
}) => {
  if (!isVisible) {
    return null
  }

  return (
    <Flex
      sx={{
        mt: 3,
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {isLoading ? (
        <SeedlingLoader />
      ) : (
        <Button type="submit" variant="primary" sx={{ px: 5 }}>
          {shouldRetryStripePayment ? "Retry Stripe Payment" : "Place Order"}
        </Button>
      )}
      <Text
        variant="fineprint"
        sx={{
          color: "white",
          opacity: 0.9,
          mt: 3,
          textAlign: "center",
        }}
      >
        * Please do not close or refresh this page while your order is being
        processed <br />
        {shouldRetryStripePayment
          ? "If you leave this page before your payment is successfully processed, your order will be voided"
          : null}
      </Text>
    </Flex>
  )
}
OrderFormButton.propTypes = {
  isLoading: PropTypes.bool,
  isVisible: PropTypes.bool,
  shouldRetryStripePayment: PropTypes.bool,
}

const _OrderForm = ({
  product,
  selectionState,
  updateOrderDetails,
  containerStyles,
  containerRef,
}) => {
  const { updateAlertState } = useFormAlertContext()
  const [stripePaymentIntentData, updateStripePaymentIntentData] = useState({
    id: null,
    clientSecret: null,
  })
  const [stripePaymentState, updateStripePaymentState] = useState({
    shouldRetry: false,
    orderNumber: null,
    orderId: null,
  })

  const voidOrder = orderId => {
    ApiClient.put("/api/orders", {
      orderId,
      pipelineStageName: Crystallize.PipelineStage.VOIDED,
    })
  }
  useEffect(() => {
    if (stripePaymentState.shouldRetry && stripePaymentState.orderId) {
      const voidOrderListener = () => voidOrder(stripePaymentState.orderId)
      window.addEventListener("beforeunload", voidOrderListener)
      return () => {
        window.removeEventListener("beforeunload", voidOrderListener)
      }
    }
  }, [stripePaymentState.shouldRetry, stripePaymentState.orderId])
  const stripe = useStripe()
  const elements = useElements()

  const bgImage = seedlingsAndSoilBackground()

  const { variantSku, quantity, cost } = selectionState
  const selectedVariant = product.variants[variantSku]
  const cart = [
    {
      product,
      selectedVariant,
      quantity,
    },
  ]
  const shippingRequired = !selectedVariant.attributes.onlineAccess

  const placeOrder = useMutation(
    ({ payload }) => ApiClient.post("/api/orders", payload),
    {
      onSuccess: (data, { values }) => {
        if (values.paymentMode === Crystallize.PaymentMode.STRIPE) {
          confirmStripePayment.mutate({
            values,
            orderNumber: data.orderNumber,
            orderId: data.orderId,
          })
        } else {
          const shippingCost = calculateShippingCost(
            values.shippingAddress.country
          )
          const orderTotal = cost + shippingCost
          updateOrderDetails({
            orderNumber: data.orderNumber,
            orderTotal,
            paymentMode: values.paymentMode,
          })
        }
      },
      onError: () => {
        updateAlertState({
          result: "error",
          msg: "Something seems to have gone wrong. Please try again!",
          isVisible: true,
        })
      },
    }
  )
  const confirmStripePayment = useMutation(
    async ({ values, orderNumber }) => {
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        stripePaymentIntentData.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: `${values.firstName} ${values.lastName}`,
              email: values.email,
              phone: `${values.countryCallingCode} ${values.phone}`,
            },
            metadata: {
              orderNumber,
            },
          },
        }
      )

      // According to Stripe docs: https://stripe.com/docs/payments/payment-intents/verifying-status
      // When it completes successfully and returns a PaymentIntent, the status is always succeeded
      if (error || paymentIntent.status !== "succeeded") {
        throw error
      }
    },
    {
      onSuccess: (_, { values, orderId, orderNumber }) => {
        setOrderToPaid.mutate({
          orderId,
          orderNumber,
          paymentMode: values.paymentMode,
        })
      },
      onError: (error, { orderNumber, orderId }) => {
        updateAlertState({
          result: "error",
          msg: `Your order has already been placed, but Stripe payment was unsuccessful: ${error.message} Please retry your payment!`,
          isVisible: true,
        })

        updateStripePaymentState({
          shouldRetry: true,
          orderNumber,
          orderId,
        })
      },
    }
  )

  const setOrderToPaid = useMutation(
    ({ orderId }) =>
      ApiClient.put("/api/orders", {
        orderId,
        pipelineStageName: Crystallize.PipelineStage.PAID,
      }),
    {
      onSuccess: (_, { orderNumber, paymentMode }) => {
        updateOrderDetails({
          orderNumber,
          paymentMode,
          orderStageUpdateFailed: false,
        })
      },
      onError: (_, { orderNumber, paymentMode }) => {
        updateOrderDetails({
          orderNumber,
          paymentMode,
          orderStageUpdateFailed: true,
        })
      },
    }
  )

  return (
    <ResponsiveImage
      isBackgroundImage={true}
      image={bgImage}
      bgImgStyles={{
        backgroundSize: "auto 900px",
        backgroundRepeat: "repeat-x",
        backgroundPosition: "top",
      }}
    >
      <Formik
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          countryCallingCode: "Singapore (+65)",
          phone: "",
          paymentMode: Crystallize.PaymentMode.PAYNOW_PAYLAH,
          stripeDetailsValid: false,
          shippingAddress: {
            country: "Singapore",
            city: "",
            streetAddress: "",
            unitNumber: "",
            postalCode: "",
          },
        }}
        validate={values => {
          const errors = {}

          if (
            values.paymentMode === Crystallize.PaymentMode.STRIPE &&
            (!values.stripeDetailsValid || !stripePaymentIntentData.id)
          ) {
            errors.stripeDetailsValid = "Invalid stripe details"
          }

          return errors
        }}
        validationSchema={Yup.object({
          firstName: Yup.string().required("Required"),
          lastName: Yup.string().required("Required"),
          email: Yup.string()
            .email("Please provide a valid email address")
            .required("Required"),
          countryCallingCode: Yup.string()
            .matches(/\+[0-9]*/)
            .required("Required"),
          phone: Yup.string()
            .matches(/[0-9]+/, "Please provide a valid phone number")
            .required("Required"),
          paymentMode: Yup.string()
            .matches(
              new RegExp(
                `(${Crystallize.PaymentMode.PAYNOW_PAYLAH}|${Crystallize.PaymentMode.STRIPE})`
              )
            )
            .required("Required"),
          ...(selectedVariant.attributes.onlineAccess
            ? {}
            : {
                shippingAddress: Yup.object({
                  country: Yup.string().required("Required"),
                  city: Yup.string(),
                  streetAddress: Yup.string().required("Required"),
                  unitNumber: Yup.string(),
                  postalCode: Yup.string().required("Required"),
                }),
              }),
        })}
        onSubmit={async values => {
          const action = "orderjournazine"
          withRecaptcha(action, async token => {
            const payload = {
              recaptchaToken: token,
              action,
              customer: {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                phone: `${values.countryCallingCode} ${values.phone}`,
                ...(selectedVariant.attributes.onlineAccess
                  ? {}
                  : {
                      shippingAddress: values.shippingAddress,
                    }),
              },
              paymentMode: values.paymentMode,
              paymentIntentId: stripePaymentIntentData.id,
              cart,
            }
            stripePaymentState.shouldRetry
              ? confirmStripePayment.mutate({
                  values,
                  orderNumber: stripePaymentState.orderNumber,
                  orderId: stripePaymentState.orderId,
                })
              : placeOrder.mutate({ values, payload })
          })
        }}
      >
        <Form
          sx={{
            pt: "110px",
            background: t => `
             linear-gradient(to bottom,
              transparent,
              ${transparentize("darkSoilBrown", 0.7)(t)} 600px,
              ${transparentize("darkSoilBrown", 0)(t)} 800px
              )

              `,
          }}
          ref={containerRef}
          tabIndex="0"
        >
          <div sx={{ ...containerStyles, pb: 5 }}>
            <Flex
              sx={{
                flexDirection: ["column", "column", "column", "row"],
                flexWrap: "wrap",
              }}
            >
              <Grid columns={[1, 2, 2, 3]} gap={3} sx={{ flex: 2 }}>
                <BaseFields />
                {shippingRequired ? <ShippingAddressFields /> : null}
                <PaymentModeFields shippingRequired={shippingRequired} />
                <StripePaymentFields
                  cart={cart}
                  shouldCreatePaymentIntent={!stripePaymentIntentData.id}
                  updateStripePaymentIntentData={updateStripePaymentIntentData}
                />
              </Grid>
              <OrderTotal
                product={product}
                productCost={cost}
                containerStyles={{
                  flex: 1,
                  ml: [0, 0, 0, "40px"],
                  minWidth: [null, null, null, "30vw"],
                }}
              />
            </Flex>
            <OrderFormButton
              isVisible={
                stripePaymentIntentData.id
                  ? !(placeOrder.isSuccess && confirmStripePayment.isSuccess)
                  : !placeOrder.isSuccess
              }
              isLoading={
                placeOrder.isLoading ||
                confirmStripePayment.isLoading ||
                setOrderToPaid.isLoading
              }
              shouldRetryStripePayment={stripePaymentState.shouldRetry}
            />
          </div>
        </Form>
      </Formik>
    </ResponsiveImage>
  )
}
_OrderForm.propTypes = {
  product: PropTypes.shape(TransformedCrystallizeProductPropTypes),
  selectionState: PropTypes.shape({
    cost: PropTypes.number,
    variantSku: PropTypes.string,
    quantity: PropTypes.number,
  }),
  updateOrderDetails: PropTypes.func,
  containerStyles: PropTypes.object,
  containerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
}

export const OrderForm = withStripe(_OrderForm)
